import {
    useMutation,
    useQueryClient,
    type UseMutateFunction,
} from '@tanstack/react-query';

import { toast, toastError } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { isTradingAuthorityRequiredError } from '@/features/tradingSession/clientAuthority';
import { useSpeedBackend } from '@/features/wallet';
import type {
    UpdateMarginRequest,
    UpdateMarginResponse,
} from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

const UPDATE_MARGIN_ERROR_TOAST_MS = 10_000;

interface UseUpdateMarginResult {
    /**
     * Fires `POST /speed/trade/update-margin`. Per-call callbacks are still
     * accepted (the caller collapses its editor from `onSuccess`); they run in
     * addition to the toasts/invalidation below.
     */
    mutate: UseMutateFunction<UpdateMarginResponse, Error, UpdateMarginRequest>;
    /** True while the (agent-signing) POST is in flight. */
    isPending: boolean;
}

/**
 * Adds margin to the user's already-open isolated position from the live
 * position view. Only ever called from an explicit user action, so BOTH
 * outcomes toast. On success the position, balance and trades queries are
 * invalidated: the position query is where the new margin and liquidation
 * price come from, the balance query is where the money left, and the trade
 * row carries the collateral the history shows.
 *
 * The endpoint is rate-limited 6/min server-side; the caller must keep its Add
 * control disabled while `isPending` instead of retrying on its own.
 */
export function useUpdateMargin(): UseUpdateMarginResult {
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();
    const networkId = useTokenPairStore((s) => s.networkId);

    const { mutate, isPending } = useMutation({
        retry: false,
        mutationFn: async (body: UpdateMarginRequest) => {
            if (!backend) {
                throw new Error('Not authenticated.');
            }
            return backend.updateMargin(body);
        },
        onSettled: (_result, _error, body) => {
            // A transport timeout can still mean Hyperliquid applied the top-up.
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.position(body.coin),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.positions(),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.balance(),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.active(networkId ?? 0),
            });
        },
        onSuccess: (result) => {
            toast.success(`Added $${result.addedUsd} of margin.`);
        },
        onError: (error) => {
            // no trading window means the request never left the browser and
            // the prompt to open one is already on screen. Nothing failed, so
            // this is a neutral note, not a red "did not complete".
            if (isTradingAuthorityRequiredError(error)) {
                toast.info(
                    'Margin unchanged. Start a trading window to add margin.',
                );
                return;
            }
            toastError(error, {
                operation: 'Add margin',
                duration: UPDATE_MARGIN_ERROR_TOAST_MS,
            });
        },
    });

    return { mutate, isPending };
}
